<template>
  <div id="app">
    <header>
      <h1>
        <router-link :to="{ name: 'index' }">
          {{ siteName }}
        </router-link>
      </h1>
      <nav>
        <router-link :to="{ name: 'info' }">
          Info
        </router-link>
        <button @click="toggleMapVisibility">
          {{ mapVisible ? 'Liste' : 'Karte' }}
        </button>
      </nav>
    </header>
    <keep-alive>
      <router-view />
    </keep-alive>
    <portal-target name="modal" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { socialUrl } from './util/helpers'

export default {
  name: 'App',
  data() {
    return {
      appTitle: process.env.VUE_APP_TITLE
    }
  },
  computed: {
    ...mapState(['settings', 'loading', 'mapVisible']),
    siteName() {
      return this.appTitle || this.settings.meta_title || ''
    }
  },
  methods: {
    ...mapActions(['toggleMapVisibility'])
  },
  created() {
    this.$store.dispatch('fetchData')
  },
  metaInfo() {
    const titleTemplate = (chunk) =>
      chunk ? `${chunk} — ${this.siteName}` : this.siteName
    const urlTemplate = (path) =>
      path.includes('//') ? path : `${window.location.origin}${path}`

    const title = ''
    const path = this.$route.path
    const description = this.settings.meta_description || ''
    const image = socialUrl(this.settings.meta_image)

    return {
      title,
      titleTemplate,
      link: [{ rel: 'canonical', href: urlTemplate(path) }],
      meta: [
        { vmid: 'description', name: 'description', content: description },
        // Facebook
        {
          vmid: 'og:url',
          name: 'og:url',
          content: path,
          template: urlTemplate
        },
        {
          vmid: 'og:title',
          name: 'og:title',
          content: title,
          template: titleTemplate
        },
        {
          vmid: 'og:description',
          name: 'og:description',
          content: description
        },
        { vmid: 'og:type', name: 'og:type', content: 'website' },
        { vmid: 'og:image', name: 'og:image', content: image },
        // Twitter
        {
          vmid: 'twitter:card',
          name: 'twitter:card',
          content: 'summary_large_image'
        },
        {
          vmid: 'twitter:title',
          name: 'twitter:title',
          content: title,
          template: titleTemplate
        },
        {
          vmid: 'twitter:description',
          name: 'twitter:description',
          content: description
        },
        { vmid: 'twitter:image', name: 'twitter:image', content: image }
      ]
    }
  }
}
</script>

<style scoped>
header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  padding-left: var(--page-padding);
  padding-top: var(--page-padding);
  pointer-events: none;
  z-index: var(--zindex-header);
  @mixin type-smaller;
  user-select: none;
  @mixin breakpoint-medium {
    margin-top: -0.35em;
  }
}
nav {
  display: inline-block;
  @mixin type-smaller;
  vertical-align: baseline;
  @mixin type-medium-lineheight;
  > * {
    pointer-events: auto;
    display: inline-block;
    margin-left: 1.25em;
  }
}
h1 {
  display: inline-block;
  @mixin type-jumbo;
  vertical-align: baseline;
  @mixin type-medium-lineheight;
}
@mixin breakpoint-medium {
  button {
    display: none;
  }
}
@media only print {
  nav {
    display: none;
  }
}
@media only print {
  header {
    position: static;
    padding-left: 0;
  }
}
</style>

<style>
@import 'sanitize.css';
@import 'reset-css';
@import './styles/fonts.css';

/* Global styles */

:root {
  --header-height: 0;
  --page-padding: 12px;
  --page-edge: calc(2 * var(--page-padding));
  --content-width: calc(100vw - var(--page-edge));
  --content-width-main: var(--content-width);
  --content-width-aside: var(--content-width);
  --content-indent: 1.75rem;
  --filter-label-width: 3.75rem;

  --color-background: rgb(255, 255, 255);
  --color-background-inverted: rgb(246, 79, 86);
  --color-text: rgb(0, 0, 0);
  --color-text-light: rgb(200, 200, 200);
  --color-text-inverted: rgb(255, 255, 255);
  --color-highlight: rgb(246, 79, 86);
  --color-highlight-light: hsl(357, 90%, 85%);
  --color-highlight-inverted: rgb(255, 255, 255);

  --zindex-content: 10;
  --zindex-map: 20;
  --zindex-filter: 30;
  --zindex-header: 40;
  --zindex-modal: 50;

  --font-sans: Acronym, Helvetica, sans-serif;
  --font-serif: TimesNRMTStd, Times New Roman, Times, serif;
  --font-serif-cond: TimesNRMTStd-Cond, Times New Roman, Times, serif;

  @mixin breakpoint-small {
    --page-padding: 18px;
  }
  @mixin breakpoint-medium {
    --page-padding: 24px;
    --content-width-main: calc(var(--content-width) * 0.55);
    --content-width-aside: calc(var(--content-width) * 0.45);
  }
  @mixin breakpoint-large {
    --page-padding: 30px;
    --content-indent: 1.9rem;
    --content-width-main: calc(var(--content-width) * 0.6);
    --content-width-aside: calc(var(--content-width) * 0.4);
  }
  @mixin breakpoint-xlarge {
    --content-width-main: calc(var(--content-width) * 2 / 3);
    --content-width-aside: calc(var(--content-width) * 1 / 3);
  }
}

body {
  margin: 0;
  padding: 0;
  font-family: var(--font-sans);
  padding-left: var(--page-padding);
  padding-right: var(--page-padding);
  padding-bottom: var(--page-padding);
  @mixin type-medium;
}

b,
strong {
  font-weight: bold;
}

i,
em {
  font-style: italic;
}

a:link,
a:visited,
a:hover,
a:active {
  color: inherit;
  text-decoration: none;
}

fieldset {
  min-width: 0;
  width: 100%;
  border: none;
  padding: 0;
}

@-moz-document url-prefix() {
  fieldset {
    display: table-cell;
  }
}

button {
  appearance: none;
  overflow: visible;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  color: inherit;
  background: transparent;
  font: inherit;
  line-height: normal;
  text-decoration: none;
  text-align: inherit;
  cursor: pointer;
  user-select: none;
  transition: transition(color);
}

/* Remove mystery padding in Gecko browsers */
button::-moz-focus-inner {
  padding: 0;
  border: 0;
}
select {
  width: 100%;
  border: none;
  border-radius: 0;
  outline: none;
  margin: 0;
  padding: 0;
  box-shadow: none;
  background: transparent;
  background-image: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  text-indent: 0.01px;
  text-overflow: '';
  position: relative;
  left: 1px;
  transition: transition(color);
  cursor: pointer;
}
@-moz-document url-prefix() {
  select {
    left: -2px;
  }
}
option {
  padding-left: 0;
  padding-bottom: 0;
}
@mixin breakpoint-all-only {
  select,
  input,
  textarea {
    font-size: 16px !important;
  }
}

.sr-only {
  position: absolute !important;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.icon path {
  &[stroke] {
    stroke: currentColor;
  }
}

/**
 * Lazyloaded media
 */

.lazyload,
.lazyloading {
  opacity: 0.15;
  background-color: rgb(0, 0, 0);
}

.lazyloaded {
  opacity: 1;
  transition: 400ms opacity;
}

@media only print {
  :root {
    --color-text-light: rgb(100, 100, 100);
    --page-padding: 20px;
  }
  @page {
    margin: 0;
  }
}
</style>
